import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { useLink } from "@react-aria/link"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import { Icon } from "components/icon/Icon"
import { useRetailer } from "components/retailer/Context"
import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useNavigation } from "contexts/Navigation"

import { usePage } from "contexts/Page"
import * as button from "css/buttons"
import { legend100, normal } from "css/text"
import { useRef } from "react"

export const linkStyles = css`
  /* Types */
  &.text {
    ${button.buttonText}
  }
  &.filled {
    ${button.buttonFilled}
  }
  &.icon {
    ${button.buttonIcon}
  }
  &.inline {
    ${button.buttonInline}
  }

  /* Styles */
  &.green {
    ${button.green}
  }
  &.opaque-green {
    ${button.opaqueGreen}
  }
  &.opaque-white {
    ${button.opaqueWhite}
  }
  &.translucent-dark {
    ${button.translucentDark}
  }
  &.translucent-light {
    ${button.translucentLight}
  }

  /* Sizes */
  &.small {
    ${legend100}
    padding-inline: 1.5625rem;
  }
  &.smallnormal {
    ${legend100}
    ${normal}
  }

  &.dark-theme {
    --text: rgb(var(--pure-white));
  }
  &.light-theme {
    --text: rgb(var(--light-black));
  }
`

const LinkStyled = styled.a`
  ${linkStyles}
`

export default function Link(props) {
  const {
    tabIndex,
    aria_hidden,
    aria_label,
    aria_describedby,
    children,
    label,
    className: c,
    theme,
    disabled,
    external,
    href,
    icon,
    iconColor,
    onClick,
    style,
    download,
    download_position_name,
    outlink_name: outlinkName,
    eventNameRetailer,
  } = props
  const page = usePage()
  const retailer = useRetailer()
  const console = useConsole()
  const navigation = useNavigation()
  const rlink = useRef()
  const env = useEnv()
  const { linkProps } = useLink(
    {
      isDisabled: disabled,
      ...props,
    },
    rlink
  )

  const navigate = e => {
    e.preventDefault()
    navigation.navigate(e.currentTarget.getAttribute("href"))
  }

  const DEFAULT = "inline green"

  const i = icon ?? "chevron"
  const className = [
    c ?? style ?? DEFAULT,
    (i === "chevron" || i === "externalLink") && "reverseIcon",
    disabled && "disabled",
    (!href || href === "") && !external && "fake-link",
    theme?.theme,
  ]
    .filter(Boolean)
    .join(" ")

  const isInline = className => className?.includes("inline")
  console.verbose("Link(%o)", props)

  // Should we remove this?
  if (download) {
    return (
      <LinkStyled
        {...linkProps}
        as={"a"}
        tabIndex={tabIndex}
        aria-hidden={aria_hidden}
        aria-label={aria_label || undefined}
        aria-describedby={aria_describedby}
        className={className}
        href={download}
        download
        target='_blank'
        ref={rlink}
      >
        {icon && icon !== "none" && <Icon type={icon ?? "chevron"} fill={iconColor} />}
        {label || children}
      </LinkStyled>
    )
  }

  if (external) {
    // Replace .org global domain w/ CN domain when applicable
    // Add origin on relative paths (ie. PDFs etc.)
    const href = new URL(external.replaceAll(env.orgGlobalDomain, env.org), `https://${env.cloudinary.secureDistribution}`)

    const triggerEvent = () => {
      if (icon === "download") {
        waTriggerEvent({
          eventName: "download",
          eventType: "Click",
          downloadInfo: {
            position: download_position_name || "",
            type: "pdf",
            fileName: href.href,
            size: "",
          },
        })
      } else if (retailer?.rswi) {
        waTriggerEvent({
          eventName: eventNameRetailer,
          eventType: "Click",
          retailerInfo: {
            id: retailer?.rswi?.toUpperCase() || "",
            interaction: localStorage?.getItem?.("rlx_ddl_retailer_interaction") || "retailerWebsiteVisit",
            previewsComponent: global.location.pathname.includes("/service-centre") ? "service locator map page" : "store locator map page",
            type: "store",
          },
        })
      } else {
        waTriggerEvent({
          eventName: "outlink",
          eventType: "Click",
          outlinkInfo: {
            linkURL: href.href,
            linkName: outlinkName,
          },
        })
      }
    }

    return (
      <LinkStyled
        {...linkProps}
        aria-hidden={aria_hidden}
        aria-label={aria_label || undefined}
        aria-describedby={aria_describedby}
        as={external ? "a" : "span"}
        className={className}
        href={external ? href : null}
        onClick={onClick || triggerEvent}
        ref={rlink}
        rel='noopener noreferrer'
        tabIndex={tabIndex}
        target='_blank'
      >
        {icon !== "none" && <Icon type={icon ?? "externalLink"} fill={iconColor} />}
        {label || children}
      </LinkStyled>
    )
  }

  return (
    <LinkStyled
      {...linkProps}
      aria-hidden={aria_hidden}
      aria-label={aria_label || undefined}
      aria-describedby={aria_describedby}
      as={href ? "a" : "span"}
      className={className}
      href={href ? navigation.localize(href) : null}
      onClick={onClick || navigate}
      ref={rlink}
      tabIndex={tabIndex}
    >
      {!isInline(className) && icon !== "none" && <Icon type={icon ?? "chevron"} fill={iconColor} />}
      {label || children}
      {isInline(className) && icon !== "none" && <Icon type={icon ?? "chevron"} fill={iconColor} />}
    </LinkStyled>
  )
}
